import React, { useMemo } from 'react';
import {
  Typography,
  getTypographyDefaultColor,
  Space,
  Col,
  Row,
  Container,
  SpanConfig
} from '@ag1/ui';
import clsx from 'clsx';
import { QuoteLander, SanityKeyed } from 'types/studio/studio';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { StudioBaseImageV2 } from '../StudioBaseImage/StudioBaseImage';
import { CommonSectionProps } from '../types';
import styles from './Quote.module.scss';

const QuoteBlockMarkup = ({
  _key: key,
  variant,
  headline,
  image,
  imgRightAligned,
  quote,
  background,
  theme,
  author,
  authorDescription,
  footnote,
  padding,
  visibleOnPageLoad
}: SanityKeyed<QuoteLander> & CommonSectionProps) => {
  const imageColConfig = useMemo(
    () =>
      (variant === 'image_broad'
        ? { xxs: 12, s: 6 }
        : { xxs: 12, s: 6, l: 4 }) satisfies SpanConfig,
    [variant]
  );

  const infoColConfig = useMemo(
    () =>
      (variant === 'image_broad'
        ? { xxs: 12, m: 6, l: { value: 5, wide: true }, xl: 5 }
        : { xxs: 12, m: 6, l: { value: 7, wide: true }, xl: 7 }) satisfies SpanConfig,
    [variant]
  );

  return (
    <Space
      top={{ xxs: 40, s: 40, m: 40, l: 40 }}
      bottom={{ xxs: 40, s: 40, m: 40, l: 40 }}
      className={clsx(getSectionPaddingClasses(styles, padding))}>
      <div className={clsx(background && styles[background])}>
        <Container narrow id={key} className={styles.container}>
          <Row className={styles.row}>
            <Col span={12}>
              <Row align={{ l: 'between' }}>
                {headline && (
                  <Col span={{ xxs: 12 }}>
                    <Typography
                      variant="headlineSans"
                      size="2"
                      component="h2"
                      color={getTypographyDefaultColor(theme)}
                      className={clsx(styles.headline, imgRightAligned && styles.headlineVariant)}
                      noPadding>
                      {headline}
                    </Typography>
                  </Col>
                )}
                {image && image.asset && (
                  <Col
                    span={imageColConfig}
                    className={clsx(styles.col_img, imgRightAligned && styles.imgRightAligned)}>
                    <StudioBaseImageV2
                      image={image}
                      sizes={imageColConfig}
                      eagerLoading={visibleOnPageLoad}
                      className={clsx(
                        styles.img_wrap,
                        variant === 'image_broad' && styles.img_wrap_broad
                      )}
                      childClassName={styles.img}
                    />
                  </Col>
                )}
                <Col span={infoColConfig} className={styles.col_info}>
                  <div className={clsx(styles.infoBroad, imgRightAligned && styles.infoNarrow)}>
                    <Typography
                      noPadding
                      component="p"
                      variant="subtitle"
                      size="2"
                      color={getTypographyDefaultColor(theme)}
                      className={clsx(styles.quote, imgRightAligned && styles.quoteVariant)}>
                      {quote}
                    </Typography>
                    <Typography
                      variant="body"
                      size="2"
                      weight="700"
                      color={getTypographyDefaultColor(theme)}
                      className={clsx(styles.author, imgRightAligned && styles.authorVariant)}
                      component="p"
                      noPadding>
                      {author}{' '}
                      <span className={clsx(imgRightAligned ? styles.descrVariant : styles.descr)}>
                        {authorDescription}
                      </span>
                    </Typography>
                  </div>
                </Col>
              </Row>
              {footnote && (
                <Typography
                  variant="portalCaption"
                  size="2"
                  color="grey-1-new"
                  noPadding
                  className={styles.footnote}>
                  <CustomPortableText value={footnote} />
                </Typography>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </Space>
  );
};

export default QuoteBlockMarkup;
